import React from "react";
import { useNavigate } from "react-router-dom";

const CompanyDetailsCard = ({ company }) => {
  const navigate = useNavigate();
  const data = company?.data;

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="w-24 h-24 mb-6 rounded-full bg-slate-100 dark:bg-slate-200/20 flex items-center justify-center">
          <i className="fa-regular fa-building text-4xl text-slate-300 dark:text-gray-100"></i>
        </div>
        <h3 className="text-xl font-bold text-slate-800 dark:text-gray-200">
          No Company Selected
        </h3>
        <p className="text-sm text-slate-500 dark:text-gray-300 text-center mt-2">
          Select a company to view detailed information.
        </p>
      </div>
    );
  }

  const jobs = data?.jobData || [];

  return (
    <div className="space-y-5">
      {/* ================= HERO SECTION ================= */}
      <div className="relative rounded-2xl overflow-hidden border border-slate-200 dark:border-gray-600">
        {data.coverImage?.url && (
          <img
            src={data.coverImage.url}
            alt="Cover"
            className="w-full h-40 object-cover"
          />
        )}

        <div className="p-6 bg-white dark:bg-white/10">
          <div className="flex items-center gap-4">
            {data.logo?.url && (
              <img
                src={data.logo.url}
                alt="Logo"
                className="w-20 h-20 rounded-xl object-cover border shadow-sm"
              />
            )}

            <div>
              <h2 className="text-xl font-bold text-slate-800 dark:text-white">
                {data.content?.en?.title}
              </h2>
              <p className="text-sm text-slate-500 dark:text-gray-300">
                {data.firstName} {data.lastName}
              </p>
              <p className="text-xs text-green-600 font-medium mt-1 capitalize">
                {data.status}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ================= DESCRIPTION ================= */}
      {data.content?.en?.content && (
        <div className="bg-white dark:bg-white/10 border border-slate-200 dark:border-gray-600 rounded-xl p-6">
          <h3 className="text-lg font-semibold mb-3 text-slate-800 dark:text-white">
            About Company
          </h3>
          <p className="text-sm text-slate-600 dark:text-gray-300 leading-relaxed">
            {data.content.en.content}
          </p>
        </div>
      )}

      {/* ================= CONTACT ================= */}
      <div className="bg-white dark:bg-white/10 border border-slate-200 dark:border-gray-600 rounded-xl p-6 space-y-3">
        <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
          Contact Information
        </h3>

        {data.contactEmail && (
          <div className="flex items-center gap-3 text-sm text-slate-600 dark:text-gray-300">
            <i className="fa-solid fa-envelope text-palatte dark:text-white"></i>
            {data.contactEmail}
          </div>
        )}

        {Array.isArray(data.address) && data.address.length > 0 && (
          <div className="flex items-start gap-3 text-sm text-slate-600 dark:text-gray-300">
            <i className="fa-solid fa-location-dot text-palatte dark:text-white mt-1"></i>
            <div>
              {data.address.map((addr, i) => (
                <div key={i}>
                  {[addr.city, addr.state, addr.country]
                    .filter(Boolean)
                    .join(", ")}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ================= JOB LIST ================= */}
      <div className="bg-white dark:bg-white/10 border border-slate-200 dark:border-gray-600 rounded-xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
            Open Positions ({jobs.length})
          </h3>

          {jobs.length > 0 && (
            <button
              onClick={() => navigate("/")}
              className="text-sm font-medium text-palatte dark:text-white hover:underline"
            >
              See All Jobs →
            </button>
          )}
        </div>

        {jobs.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-gray-400">
            No active jobs available.
          </p>
        ) : (
          <div className="space-y-3">
            {jobs.slice(0, 6).map((job) => (
              <div
                key={job._id}
                className="p-4 rounded-lg border border-slate-200 dark:border-gray-500 hover:shadow-md transition-all  bg-slate-50 dark:bg-white/5"
              >
                <h4 className="font-semibold text-slate-800 dark:text-white">
                  {job.content?.en?.title}
                </h4>

                <div
                  className="text-xs text-slate-500 dark:text-gray-200 mt-2 line-clamp-2"
                  dangerouslySetInnerHTML={{
                    __html: job.content?.en?.content || "",
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyDetailsCard;
